import React, { useMemo } from 'react';
import { Stack, Typography } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { EncoreFormRenderer } from '@locus-taxy/encore-form';
import type { FormRendererRef } from '@locus-taxy/encore-form';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';
import type { ProfileViewMode, ViewField } from './profileTypes';
import { ProfileViewRenderer } from './ProfileViewRenderer';
import {
  buildPersonalInfoSchema,
  buildPersonalInfoInitialData,
} from './profileFormSchemas';

interface PersonalInfoSectionProps {
  readonly mode: ProfileViewMode;
  readonly entityData: Record<string, unknown> | undefined;
  readonly clientId: string;
  readonly formRef: React.RefObject<FormRendererRef | null>;
  readonly onChange?: (data: Record<string, unknown>) => void;
  readonly viewFields: ViewField[];
}

export function PersonalInfoSection({
  mode,
  entityData,
  clientId,
  formRef,
  onChange,
  viewFields,
}: PersonalInfoSectionProps) {
  const { t } = useTranslation();

  const schema = useMemo(() => buildPersonalInfoSchema(t), [t]);
  const formInitialData = useMemo(
    () => buildPersonalInfoInitialData(entityData),
    [entityData]
  );
  const additionalContext = useMemo(() => ({ entityData }), [entityData]);

  return (
    <div
      className="rounded-md border border-border bg-card"
      data-testid={getTestId({
        action: TestIdAction.VIEW,
        object: 'personal-info-section',
        page: 'account',
      })}
    >
      <Stack gap={3} className="p-8">
        <Typography variant="h6" fontWeight="semibold">
          {t(
            'dashboard.userDetails.personalInformation',
            'Personal Information'
          )}
        </Typography>

        {mode === 'view' ? (
          <ProfileViewRenderer fields={viewFields} columns={2} />
        ) : (
          <EncoreFormRenderer
            ref={formRef}
            schema={schema as any}
            clientId={clientId}
            initialData={formInitialData}
            additionalContext={additionalContext}
            onChange={onChange}
            routeContext={{ entity: 'user', page: 'profile' }}
          />
        )}
      </Stack>
    </div>
  );
}
